/**
 * Attachment actions (DESIGN.md §18): open, download, save all. Wired into the reading pane's
 * attachment strip (`AttachmentStrip`) as its context menu (right-click, Shift+F10, long-press);
 * "Open" is also the click on an attachment. File downloads stay "Planned" until the platform API
 * serves attachment content.
 */
import { defineActions, type ActionRegistry } from '@platform/ui';
import { DownloadIcon, ExternalLinkIcon, FolderDownIcon } from 'lucide-react';

/** What the actions need from an attachment (matches `AttachmentStripItem`). */
export interface AttachmentTarget {
  id: string;
  name: string;
  /** file = file attachment, item = attached message. */
  kind: 'file' | 'item';
}

export interface AttachmentActionHandlers {
  open(attachment: AttachmentTarget): void;
  download(attachment: AttachmentTarget): void;
  saveAll(): void;
}

export interface AttachmentActionOptions {
  /** Server serves attachment content (download/open of files). */
  canDownload: boolean;
  /** Number of attachments of the message ("Save all" from 2 on). */
  count: number;
}

export function createAttachmentActions(handlers: AttachmentActionHandlers, options: AttachmentActionOptions): ActionRegistry<AttachmentTarget> {
  const surfaces = ['overflow', 'context'] as const;
  return defineActions<AttachmentTarget>([
    {
      id: 'attachment.open',
      label: 'webmail.attachmentActions.open',
      icon: ExternalLinkIcon,
      group: 'open',
      defaultGesture: true,
      surfaces,
      // Attached messages open in the reading pane; files need the download endpoint.
      enabled: ({ targets }) => targets[0]!.kind === 'item' || options.canDownload,
      run: ({ targets }) => handlers.open(targets[0]!),
    },
    {
      id: 'attachment.download',
      label: 'webmail.attachmentActions.download',
      icon: DownloadIcon,
      group: 'save',
      surfaces,
      planned: !options.canDownload,
      run: ({ targets }) => handlers.download(targets[0]!),
    },
    {
      id: 'attachment.saveAll',
      label: { key: 'webmail.attachmentActions.saveAll', params: { count: options.count } },
      icon: FolderDownIcon,
      group: 'save',
      surfaces,
      visible: () => options.count > 1,
      planned: !options.canDownload,
      run: () => handlers.saveAll(),
    },
  ]);
}
